import type { EaiwsSessionManager } from './EaiwsSessionManager';

import * as React from 'react';

import { onNewSession } from './EaiwsSessionManager';

import { wrapWithLock } from '@egr/xbox/utils/wrapper/LockWrapper';

import { EaiwsSession } from '@easterngraphics/wcf/modules/eaiws/EaiwsSession';
import { Lock } from '@easterngraphics/wcf/modules/utils/async';

export const newSessionLock = new Lock();

interface EaiwsProviderProps extends React.PropsWithChildren {
    manager: EaiwsSessionManager;
    tokenCallback?: () => Promise<string | undefined>;
    gatekeeperIdCallback: () => Promise<string>;
    sessionExpiredCallback: () => void;
    sessionInitCallback?: (session: EaiwsSession) => void;
    defaultErrorCallback?: (error: Error) => void;
    skipSessionOnRender?: boolean;
}

function handleError(error: Error, sessionExpiredCallback: () => void, defaultErrorCallback?: (error: Error) => void): void {
    if (error instanceof Error && error.name === 'SoapError' && error.message.startsWith('unknown session')) {
        sessionExpiredCallback();
    } else {
        defaultErrorCallback?.(error);
    }
}

async function initializeSession(
    manager: EaiwsSessionManager,
    token: string | undefined,
    gatekeeperId: string,
    setSession: React.Dispatch<React.SetStateAction<EaiwsSession | undefined>>,
    skipSession?: boolean
): Promise<EaiwsSession> {
    await manager.initializeSession(gatekeeperId, token, skipSession);
    const session = new EaiwsSession();
    const sessionResponse = manager.getSession();

    if (sessionResponse == null) {
        throw new Error('Failed to connect session: response was null');
    }
    const { server, sessionId, keepAliveInterval } = sessionResponse;
    if (server == null) {
        throw new Error('Failed to connect session: server was null');
    }
    if (sessionId == null) {
        throw new Error('Failed to connect session: sessionId was null');
    }
    session.connect(server, sessionId, keepAliveInterval);
    setSession(session);

    return session;
}

export const EaiwsContext = React.createContext<EaiwsSession | undefined>(undefined);
export const EaiwsProvider: React.FC<EaiwsProviderProps> = ({
    manager,
    tokenCallback,
    gatekeeperIdCallback,
    sessionInitCallback,
    sessionExpiredCallback,
    defaultErrorCallback,
    children,
    skipSessionOnRender,
}) => {
    const [eaiwsSession, setEaiwsSession] = React.useState<EaiwsSession | undefined>(undefined);

    React.useEffect(() => {
        const resetSession = wrapWithLock(async () => {
            setEaiwsSession(undefined);
        }, newSessionLock);

        onNewSession.addListener(resetSession);

        return () => {
            onNewSession.removeListener(resetSession);
        };
    }, []);

    const sessionAvailable = eaiwsSession != null;
    React.useEffect(() => {
        void wrapWithLock(async () => {
            if (!sessionAvailable) {
                try {
                    const [token, gatekeeperId] = await Promise.all([
                        tokenCallback?.(),
                        gatekeeperIdCallback()
                    ]);
                    const session = await initializeSession(manager, token, gatekeeperId, setEaiwsSession, skipSessionOnRender);
                    sessionInitCallback?.(session);
                } catch (error) {
                    handleError(error as Error, sessionExpiredCallback, defaultErrorCallback);
                }
            }
        }, newSessionLock)();
    }, [sessionAvailable]);

    return <EaiwsContext.Provider value={eaiwsSession}>{children}</EaiwsContext.Provider>;
};

export function useEaiwsSession() {
    return React.useContext(EaiwsContext);
}
